import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';

import { Observable, of } from 'rxjs';
import { catchError, finalize, map, switchMap, tap } from 'rxjs/operators';

import { AuthService } from '../services/auth.service';
import { GroupsService } from '../../groups/groups.service';
import { NotificationChannelType, SubGroupType } from '../enums/enums';
import { ChooseSubGroupComponent } from '../../groups/choose-subgroup/choose-subgroup.component';
import { LoadingService } from '../components/loading/loading.service';
import { SubgroupProceedDialogComponent } from 'src/app/profile/subgroup-proceed-dialog/subgroup-proceed-dialog.component';

@Injectable({ providedIn: 'root' })
export class GroupAccessGuard {
    constructor(
        private auth: AuthService,
        private router: Router,
        private groupsService: GroupsService,
        private dialog: MatDialog,
        private loadingService: LoadingService,
    ) { }

    canActivate(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot,
    ): Observable<boolean | UrlTree> {

        return this.groupsService.getGroupDetailsPublic(this.auth.idenediGroupId).pipe(
            switchMap((group) => {

                // If user not logged in, allow access
                if (!this.auth.isLoggedIn()) {
                    return of(true);
                }

                if (localStorage.getItem('skipSubgroupSelection') === 'true') {
                    return this.handleRequiredChannels(group, state.url);
                }

                // JS query params
                const params = new URLSearchParams(window.location.search);
                const gid = params.get('gid');

                let SubGroupsShowOnLogin =
                    group.GroupSummary?.SubGroupIdsShowOnLogin || [];

                if (SubGroupsShowOnLogin.length === 0) {
                    return this.handleRequiredChannels(group, state.url);
                }

                return this.groupsService
                    .fetchSubGroups(this.auth.idenediGroupId, true, SubGroupType.UserType)
                    .pipe(
                        switchMap((currentUserSubgroups) => {

                            // If user already has subgroup
                            if (currentUserSubgroups?.length > 0) {
                                return this.handleRequiredChannels(group, state.url);
                            }

                            // Map required subgroups with full id
                            SubGroupsShowOnLogin = SubGroupsShowOnLogin.map(sg => this.auth.idenediGroupId + '_' + sg);

                            return this.groupsService
                                .fetchSubGroups(this.auth.idenediGroupId, false, SubGroupType.UserType)
                                .pipe(
                                    switchMap((allSubgroups) => {

                                        const joinRequest = allSubgroups.find(sg => sg.Idenedi === gid);

                                        // Case: User is joining via "gid" URL param
                                        if (joinRequest) {
                                            return this.joinGroup(joinRequest.Idenedi).pipe(
                                                tap(() => console.log('Joined subgroup via URL gid')),
                                                map(() => true),
                                                catchError(() => of(false))
                                            );
                                        }

                                        // Filter only the "show on login" subgroups
                                        const filteredSubgroups = allSubgroups.filter(sg =>
                                            SubGroupsShowOnLogin.includes(sg.Idenedi)
                                        );

                                        if (filteredSubgroups.length === 0)
                                            return of(true);
                                        // --- Open ChooseSubGroup Dialog ---
                                        return new Observable<boolean>((observer) => {

                                            const chooseDialog = this.dialog.open(ChooseSubGroupComponent, {
                                                width: '62.5rem',
                                                maxHeight: '95vh',
                                                disableClose: true,
                                                data: {
                                                    subGroups: filteredSubgroups.map(sg => ({
                                                        ...sg,
                                                        GroupIdenedi: sg.Idenedi,
                                                        GroupName: sg.Profile.FirstName + ' ' + sg.Profile.LastName,
                                                        GroupMembershipOption: sg.GroupSettings.GroupMembershipOption,
                                                        isJoined: false
                                                    }))
                                                }
                                            });

                                            chooseDialog.afterClosed().subscribe((resp) => {
                                                console.log('ChooseSubGroupComponentClosed', resp);
                                                // Cleanup

                                                if (resp?.closed) {
                                                    const proceedDialog = this.dialog.open(SubgroupProceedDialogComponent, {
                                                        width: '42.5rem',
                                                        disableClose: true
                                                    });

                                                    proceedDialog.afterClosed().subscribe(() => {
                                                        console.log('SubgroupProceedDialogComponentClosed');
                                                        this.router.navigate(['/'])
                                                        observer.next(true);   // allow navigation
                                                        observer.complete();   // finish guard
                                                    });
                                                } else {
                                                    localStorage.removeItem('user.chosen.idenedi.group.id');
                                                    this.router.navigate(['/groups/choose']);
                                                    observer.next(true);   // allow navigation
                                                    observer.complete();   // finish guard
                                                }

                                            });
                                        });

                                    })
                                );
                        })
                    );
            })
        );
    }


    /** Helper function to check required notification channels */
    private handleRequiredChannels(group: any, navigateTo: string): Observable<boolean> {
        const requiredChannels = group.GroupSettings?.RequiredNotificationChannels || [];

        // If no required channels, allow access
        if (requiredChannels.length === 0) return of(true);

        return this.groupsService.getNotificationPhoneNumber(this.auth.idenediGroupId).pipe(
            switchMap((credentials: any) => {
                const isEmailRequired =
                    requiredChannels.includes(NotificationChannelType.Email) && !credentials?.Email;
                const isPhoneRequired =
                    requiredChannels.includes(NotificationChannelType.Sms) && !credentials?.Phone;

                if (isEmailRequired) {
                    this.router.navigate(['/groups/verify-email'], {
                        state: { isEmailRequired, isPhoneRequired, group, credentials, navigateTo },
                    });
                    return of(false);
                }

                if (isPhoneRequired) {
                    this.router.navigate(['/groups/verify-sms'], {
                        state: { isEmailRequired, isPhoneRequired, group, credentials, navigateTo },
                    });
                    return of(false);
                }

                // If both email & phone satisfied, allow access
                return of(true);
            })
        );
    }

    private joinGroup(groupId: string): Observable<any> {
        if (!groupId) {
            console.warn('Invalid parameters for addGroupMembers');
            return of(null);
        }

        return this.groupsService
            .joinGroup(groupId)
            .pipe(
                finalize(() => this.loadingService.loadingOff()),
                catchError((error) => {
                    console.error('Error adding group members:', error);
                    return of(null);
                })
            );
    }
}
